import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Auth slice imports
import { validateToken, validateAccountId } from "./redux/Slice/Auth/authSlice";
import { setPageLoading } from "./redux/Slice/Auth/loadingSlice";

// Pages/components
import Login from "./pages/Auth/Login";
import Home from "./pages/Home/Home";
import Inbox from "./pages/Inbox/Inbox";
import Contacts from "./pages/Contacts/Contact";
import BroadcastsPage from "./pages/Broadcasts/Broadcasts";
import CreateBroadcastPage from "./pages/Broadcasts/CreateBroadcast";
import Tasks from "./pages/Tasks/Tasks";
import Settings from "./pages/Settings/Settings";
import AiSettingsPage from "./pages/AiSettings/AiSettingsPage";
import Reports from "./pages/Reports/Reports";
import CallsPage from "./pages/Calls/CallsPage";
import { TemplatesHome } from "./pages/Templates/TemplatesHome";
import TemplateCreate from "./pages/Templates/TemplateCreate";
// eslint-disable-next-line no-unused-vars -- kept unused so the <Dashboard /> route below can be reverted, see comment there
import Dashboard from "./components/Dashboard/Dashboard";
import DashboardScratch from "./components/Dashboard/DashboardScratch";
import NotFound from "./components/NotFound/NotFound";
import Signup from "./pages/Auth/Signup";
import Welcome from "./pages/Auth/Welcome";
import ShopifyConnecting from "./pages/Auth/ShopifyConnecting";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import Setup from "./pages/Auth/Setup";
import InvitationResponse from "./pages/Auth/InvitationResponse";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import TemplateAnalyticsPage from "./pages/Templates/TemplateAnalyticsPage";

import {
  fetchUserDetails,
  fetchUserPermissions,
} from "./redux/Slice/User/userDetailsSlice";
import { fetchNotificationSettings } from "./redux/Slice/Settings/notificationSlice";
import { initializeSocket, getConnectionStatus, onConnectionChange, onDisconnectionChange, onReconnectFailedChange, retryConnection } from "./utils/socketHandler";
import connectionMonitor from "./utils/connectionMonitor";
import { initializeNotificationAudio } from "./utils/notificationUtils";
import { useUnreadTabBadge } from "./hooks/useUnreadTabBadge";
import { setupNetworkErrorHandler } from "./utils/networkErrorHandler";
import LoadingOverlay from "./utils/LoadingOverlay";
import CallManager from "./features/calling/components/CallManager";
import VersionUpdateBanner from "./components/VersionUpdateBanner/VersionUpdateBanner";
// import { startVersionPolling } from "./utils/versionChecker"; // temporarily disabled, see below
import FirstTimeConnectionRequired from "./components/ConnectionBlocker/FirstTimeConnectionRequired";
import { Box } from "@mui/material";
import { Toaster, ToastBar, toast } from "react-hot-toast";
import { TickCircle, CloseCircle, InfoCircle, Add } from "iconsax-react";
import {
  APP_TOAST_BACKGROUND,
  APP_TOAST_TEXT_COLOR,
  APP_TOAST_FONT_SIZE,
  APP_TOAST_FONT_FAMILY,
  APP_TOAST_TOP_OFFSET,
  APP_TOAST_ICON_TINTS,
  APP_TOAST_MAX_WIDTH,
} from "./config/appToast";
import Workflow from "./pages/Workflow/Workflow";
import WorkflowCreate from "./pages/Workflow/WorkflowCreate";
import ApiIntegrationPage from "./pages/Settings/ApiIntegrationPage";
import ConnectChatbotWidgetPage from "./pages/Settings/ConnectChatbotWidgetPage";
import IntegrationsManageGate from "./components/Settings/IntegrationsManageGate";
import Onboard from "./pages/Onboard/Onboard";
import { useThemeContext } from "./contexts/ThemeContext";

// After Google Sheets OAuth, server sends users to /?google_sheets=... — forward to settings once we know accountId
const PostOAuthHomeEntry = () => {
  const { user } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const googleSheets = searchParams.get("google_sheets");
  const instagram = searchParams.get("instagram");
  const shopify = searchParams.get("shopify");
  const facebook = searchParams.get("facebook");
  const selection = searchParams.get("selection");
  const msg = searchParams.get("msg");

  if (!user?.accountId) {
    return <div>Loading user info...</div>;
  }

  if (user.requiresOnboarding) {
    return <Navigate to="/setup" replace />;
  }

  if (googleSheets === "connected" || googleSheets === "error") {
    const qs = new URLSearchParams();
    qs.set("google_sheets", googleSheets);
    if (msg) qs.set("msg", msg);
    return (
      <Navigate
        to={`/home/${user.accountId}/settings?${qs.toString()}`}
        replace
      />
    );
  }

  if (instagram === "connected" || instagram === "error") {
    const qs = new URLSearchParams();
    qs.set("instagram", instagram);
    if (msg) qs.set("msg", msg);
    return (
      <Navigate
        to={`/home/${user.accountId}/settings?${qs.toString()}`}
        replace
      />
    );
  }

  if (facebook === "connected" || facebook === "error" || facebook === "select") {
    const qs = new URLSearchParams();
    qs.set("facebook", facebook);
    if (msg) qs.set("msg", msg);
    if (selection) qs.set("selection", selection);
    return (
      <Navigate
        to={`/home/${user.accountId}/settings?${qs.toString()}`}
        replace
      />
    );
  }

  if (shopify === "connected" || shopify === "error") {
    const qs = new URLSearchParams();
    qs.set("shopify", shopify);
    if (msg) qs.set("msg", msg);
    return (
      <Navigate
        to={`/home/${user.accountId}/settings?${qs.toString()}`}
        replace
      />
    );
  }

  return <Navigate to={`/home/${user.accountId}`} replace />;
};

// Protected route component that also validates account ID
const ProtectedAccountRoute = ({ element }) => {
  const { accountid } = useParams();
  const { isAuthenticated, accountValidation } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated && accountid) {
      dispatch(validateAccountId(accountid));
    }
  }, [accountid, isAuthenticated, dispatch]);

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (!accountid) {
    return <LoadingOverlay />;
  }

  if (accountValidation.isLoading || accountValidation.isValid === null) {
    return <LoadingOverlay />;
  }

  if (accountValidation.isValid === false) {
    return <NotFound />;
  }

  return element;
};

function App() {
  const dispatch = useDispatch();
  const { mode } = useThemeContext();

  const { isAuthenticated, user } = useSelector(
    (state) => state.auth
  );
  const { userDetails } = useSelector((state) => state.userDetails);

  const [isTokenChecked, setIsTokenChecked] = useState(false);
  const socketInitialized = useRef(false); // <-- Track socket init
  // setter only called from the version poller effect below, which is temporarily
  // disabled (see comment there).
  // eslint-disable-next-line no-unused-vars
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  // Shared cooldown so the 'online' handler and the connectionMonitor 'connection'
  // handler — which can both fire within moments of the same physical reconnect —
  // don't each queue their own fetchUserDetails/fetchUserPermissions/fetchNotificationSettings
  // batch. The thunks themselves are already debounced; this is defense-in-depth only.
  const lastReconnectRefetchRef = useRef(0);
  const RECONNECT_REFETCH_COOLDOWN_MS = 8000;

  // Start version poller once authenticated
  // Temporarily disabled — not needed right now (was polling GET /api/app/version
  // every 90s for every authenticated session). Uncomment to re-enable.
  // useEffect(() => {
  //   if (!isAuthenticated) return;
  //   const stop = startVersionPolling(() => setNewVersionAvailable(true));
  //   return stop;
  // }, [isAuthenticated]);

  // Reset the socket-init gate on logout so the next login in this tab
  // re-fetches user details and (re)initializes the socket for the new
  // account instead of short-circuiting on the previous session's flag.
  useEffect(() => {
    if (!isAuthenticated) {
      socketInitialized.current = false;
    }
  }, [isAuthenticated]);
  // Connection status state
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false, // Start as disconnected until socket connects
    isReconnecting: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 2 // Match connectionMonitor's maxReconnectAttempts
  });

  // Initialize notification audio on first user interaction (when authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      initializeNotificationAudio();
    }
  }, [isAuthenticated]);

  // WhatsApp-style unread badge on the browser tab (title + favicon), scoped to
  // whichever workspace's inbox is currently loaded — see useUnreadTabBadge.js.
  useUnreadTabBadge();

  // Shared by the 'online' and connectionMonitor 'connection' handlers below — both can
  // fire within moments of the same physical reconnect. The underlying thunks are already
  // debounced (5s each); this just stops the batch of 3 dispatches from being queued twice.
  const refetchCoreUserDataOnReconnect = useCallback(() => {
    const now = Date.now();
    if (now - lastReconnectRefetchRef.current < RECONNECT_REFETCH_COOLDOWN_MS) {
      return;
    }
    lastReconnectRefetchRef.current = now;
    dispatch(fetchUserDetails());
    dispatch(fetchUserPermissions());
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  // Helper function to check if user has ever connected successfully
  const hasEverConnected = () => {
    return localStorage.getItem('hasEverConnected') === 'true';
  };

  // Helper function to handle retry connection — retry in place, no page reload
  const handleRetryConnection = () => {
    retryConnection();
  };

  // Helper function to render inbox with first-time connection check
  const renderInbox = () => {
    if (!isAuthenticated) {
      return <Navigate to="/" />;
    }

    // For first-time users, require connection
    if (!hasEverConnected()) {
      if (connectionStatus.isConnected) {
        // Mark as connected for future visits
        localStorage.setItem('hasEverConnected', 'true');
        return <Inbox />;
      } else {
        return (
          <FirstTimeConnectionRequired 
            connectionStatus={connectionStatus}
            onRetry={handleRetryConnection}
          />
        );
      }
    }

    // For returning users, always allow access (with banner if disconnected)
    return <Inbox />;
  };

  // 1) Validate token on initial load
  useEffect(() => {
    const checkToken = async () => {
      // Reset refresh token failure flag on page load to allow refresh attempts
      // This is important because the flag resets on page reload, but we want to ensure
      // refresh attempts are allowed after page refresh
      const { resetRefreshTokenFailureOnLoad } = require('./utils/getAuthToken');
      resetRefreshTokenFailureOnLoad();
      
      dispatch(setPageLoading(true));
      await dispatch(validateToken());
      setIsTokenChecked(true);
      dispatch(setPageLoading(false));
    };
    checkToken();
  }, [dispatch]);

  // 2) Re-validate token only on sensitive route changes (removed excessive validation)
  // Note: Token validation on every route change removed to improve performance

  // 3) Fetch user details if needed, then init socket
  useEffect(() => {
    // Don't fetch (or init the socket) before a session exists. Dispatching
    // fetchUserDetails here while logged out used to stamp the cache/debounce
    // layer in userDetailsSlice.js with a "No token found" rejection that then
    // blocked the real post-login fetch for several seconds.
    if (!isAuthenticated) return;

    const setupSocket = async () => {
      // Already initialized? Then do nothing.
      if (socketInitialized.current) return;

      // Kick off all three background fetches in parallel (don't await — socket init doesn't need them)
      if (!userDetails) {
        // force: true — this runs the moment isAuthenticated flips to true, so
        // bypass any cached/debounced result from an earlier (pre-login) attempt.
        Promise.all([
          dispatch(fetchUserDetails({ force: true })),
          dispatch(fetchUserPermissions()),
          dispatch(fetchNotificationSettings()),
        ]);
        return;
      }

      // Now that we do have user details, initialize the socket
      try {
        const { getAccessToken } = require('./utils/tokenManager');
        const accessToken = getAccessToken();
        if (accessToken && userDetails._id) {
          initializeSocket(userDetails._id, dispatch, accessToken);
          socketInitialized.current = true;
        }
      } catch (error) {
        console.error("Error initializing socket:", error);
      }
    };

    setupSocket();
  }, [isAuthenticated, userDetails, dispatch]);

  // Initialize network error handler
  useEffect(() => {
    setupNetworkErrorHandler();
  }, []);

  // Start proactive token refresh service
  useEffect(() => {
    const { startProactiveTokenRefresh, stopProactiveTokenRefresh } = require('./utils/tokenRefreshService');
    
    // Start proactive refresh when authenticated
    if (isAuthenticated) {
      startProactiveTokenRefresh();
    }
    
    // Cleanup on unmount or when not authenticated
    return () => {
      stopProactiveTokenRefresh();
    };
  }, [isAuthenticated]);

  // Monitor internet connectivity
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Internet connection restored');
      // Refetch data when connection is restored
      refetchCoreUserDataOnReconnect();
    };

    const handleOffline = () => {
      console.log('🚫 Internet connection lost');
      // Clear any pending errors when going offline
      dispatch({ type: 'inbox/clearErrors' });
      dispatch({ type: 'userDetails/clearErrors' });
      dispatch({ type: 'inboxInfo/clearErrors' });
      dispatch({ type: 'inboxSettings/clearErrors' });
      dispatch({ type: 'inboxAll/clearErrors' });
    };

    // Add a global network status check
    const checkNetworkStatus = () => {
      if (!navigator.onLine) {
        handleOffline();
      }
    };

    // Check immediately
    checkNetworkStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch, refetchCoreUserDataOnReconnect]);

  // Auth error redirect is handled by the Axios interceptor in networkErrorHandler.js
  // No polling needed here — the interceptor fires immediately on any 401 response.

  // Monitor connection status - only when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset connection status when not authenticated
      setConnectionStatus({
        isConnected: false, // Start as disconnected
        isReconnecting: false,
        reconnectAttempts: 0,
        maxReconnectAttempts: 2 // Match connectionMonitor's maxReconnectAttempts
      });
      return;
    }

    const handleConnection = () => {
      console.log('🔄 Connection restored, updating status and refetching data');
      setConnectionStatus(prev => ({
        ...prev,
        isConnected: true,
        isReconnecting: false,
        reconnectAttempts: 0
      }));
      
      // Refetch data when connection is restored
      refetchCoreUserDataOnReconnect();
    };

    const handleDisconnection = () => {
      setConnectionStatus(prev => ({
        ...prev,
        isConnected: false,
        isReconnecting: true
      }));
    };

    const handleReconnectFailed = (data) => {
      // Update status when all reconnection attempts fail
      const status = getConnectionStatus();
      setConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: status.reconnectAttempts || (data?.reconnectAttempts ?? 0),
        maxReconnectAttempts: status.maxReconnectAttempts || (data?.maxReconnectAttempts ?? 2)
      });
    };

    // Set up event-driven connection listeners (no polling needed)
    onConnectionChange(handleConnection);
    onDisconnectionChange(handleDisconnection);
    onReconnectFailedChange(handleReconnectFailed);

    // Cleanup: Remove event listeners to prevent memory leaks
    return () => {
      connectionMonitor.off('connection', handleConnection);
      connectionMonitor.off('disconnection', handleDisconnection);
      connectionMonitor.off('reconnectFailed', handleReconnectFailed);
    };
  }, [isAuthenticated, refetchCoreUserDataOnReconnect]);

  // Show a loading overlay while token check or page loading is in progress

  return (
    <Box 
      sx={{ 
        position: "relative",
        minHeight: "100vh",
        backgroundColor: mode === 'dark' ? '#0F0F0F' : '#FAFBFC',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Toaster
        position="top-right"
        containerStyle={{
          // Must always render below the top nav (Rahim, 2026-08-31) — same
          // constant MuiSnackbar's theme override (materialTheme.js) reads,
          // so both toast systems clear the nav by the same amount.
          top: APP_TOAST_TOP_OFFSET,
          right: 16,
          zIndex: 1500,
        }}
        gutter={10}
        toastOptions={{
          duration: 6000,
          // react-hot-toast keeps a dismissed toast mounted (invisible) for
          // this long before actually removing it — defaults to 1000ms,
          // way past our 280ms exit animation, so an invisible box sat there
          // occupying layout space for the remaining ~720ms (Rahim,
          // 2026-08-31: "the toast pop up remains there for some time" —
          // this was the glitch, not the animation itself).
          removeDelay: 300,
          // New look (Fluent-style reference): colored circle icon badge +
          // neutral bold text + plain close X, replacing the old
          // colored-border/colored-text card — severity color now lives on
          // the icon only, built below per t.type instead of via
          // toastOptions.success/error.iconTheme (react-hot-toast's built-in
          // check/x icon can't be swapped to iconsax through iconTheme, only
          // its stroke color — so the icon itself is fully replaced in the
          // custom ToastBar render instead).
          style: {
            background: APP_TOAST_BACKGROUND,
            color: APP_TOAST_TEXT_COLOR,
            fontFamily: APP_TOAST_FONT_FAMILY,
            fontSize: APP_TOAST_FONT_SIZE,
            lineHeight: 1.45,
            fontWeight: 600,
            padding: "12px 14px",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
            maxWidth: APP_TOAST_MAX_WIDTH,
          },
        }}
      >
        {(t) => {
          // "blank" = a plain toast(message) call with no severity — treated
          // as info. "loading"/"custom" keep react-hot-toast's own icon
          // (spinner) since they're not one of our 4 severities. No wrapper
          // background — the "Bold" variant of these icons is already one
          // solid glyph (filled shape with the check/x/! carved out as a
          // transparent cutout, confirmed in iconsax-react's own source), so
          // color=severity alone is the whole icon (Rahim, 2026-08-31: "i
          // only want one green tick icon... why cant you get it from
          // anywhere" — it was already the library default, no wrapper
          // needed).
          const severityIcon = {
            success: { Icon: TickCircle, color: APP_TOAST_ICON_TINTS.success },
            error: { Icon: CloseCircle, color: APP_TOAST_ICON_TINTS.error },
            blank: { Icon: InfoCircle, color: APP_TOAST_ICON_TINTS.info },
          }[t.type];
          return (
            <ToastBar
              toast={t}
              // Overrides react-hot-toast's own default enter/exit animation
              // (confirmed via its source: ToastBar spreads this style prop
              // into its root wrapper AFTER its own animation, so this wins)
              // with our own keyframes (index.css) — a plain fade+scale
              // instead of the library's slide-sideways-and-tilt exit.
              style={{
                animation: t.visible
                  ? "appToastIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards"
                  : "appToastOut 0.28s cubic-bezier(0.4, 0, 1, 1) forwards",
              }}
            >
              {({ icon, message }) => (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {severityIcon ? (
                      <severityIcon.Icon size={22} variant="Bold" color={severityIcon.color} />
                    ) : (
                      icon
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>{message}</Box>
                  {t.type !== "loading" && (
                    <Box
                      onClick={() => toast.dismiss(t.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        cursor: "pointer",
                        color: "rgba(0, 0, 0, 0.45)",
                        ml: 0.5,
                        "&:hover": { color: "rgba(0, 0, 0, 0.7)" },
                      }}
                    >
                      {/* iconsax has no bare X glyph — Add rotated 45°, same
                          trick as materialTheme.js's ToastCloseIcon. */}
                      <Add size={26} variant="Linear" color="currentColor" style={{ transform: "rotate(45deg)" }} />
                    </Box>
                  )}
                </>
              )}
            </ToastBar>
          );
        }}
      </Toaster>
      {newVersionAvailable && (
        <VersionUpdateBanner onReload={() => window.location.reload()} />
      )}
      <LoadingOverlay />
      {isAuthenticated && <CallManager />}

      {isTokenChecked && (
        <Routes>
          {/* Public Route: "/" => Login or redirect if already authenticated */}
          <Route
            path="/"
            element={
              !isAuthenticated ? (
                <Login />
              ) : user?.accountId ? (
                <PostOAuthHomeEntry />
              ) : (
                // If authenticated but no accountId in user, show fallback
                <div>Loading user info...</div>
              )
            }
          />

          <Route path="/signup" element={<Signup />} />

          {/* Shopify-origin install with an already-confirmed active App Pricing subscription
              lands here instead of /signup directly — see shopifyOAuthController.js's callback. */}
          <Route path="/welcome" element={<Welcome />} />

          {/* First-time Shopify shop lands here right after OAuth (App Store 2.3.3 fix) */}
          <Route path="/shopify/connecting" element={<ShopifyConnecting />} />

          {/* Public Route: Email verification link sent at signup - logs the user in directly */}
          <Route path="/verify-email/:token" element={<VerifyEmail />} />

          {/* Post-signup onboarding: org/business/personal info + plan, moved out of Signup */}
          <Route path="/setup" element={isAuthenticated ? <Setup /> : <Navigate to="/" />} />

          {/* Public Route: Invitation response (must be before protected routes) */}
          <Route path="/invitation-response/:action/:email" element={<InvitationResponse />} />

          {/* Public Route: Forgot Password */}
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Public Route: Reset Password */}
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected Route: /home/:accountid with account validation */}
          <Route
            path="/home/:accountid"
            element={<ProtectedAccountRoute element={<Home />} />}
          >
            {/* Dashboard button now points at the scratch redesign; original <Dashboard /> code kept but unconnected, switch back by reverting this line */}
            <Route index element={<DashboardScratch />} />
            <Route
              path="/home/:accountid/inbox/:userid"
              element={renderInbox()}
            />
            <Route
              path="/home/:accountid/inbox/:userid/chat/:contactid"
              element={renderInbox()}
            />

            <Route
              path="templates"
              element={
                isAuthenticated ? <TemplatesHome /> : <Navigate to="/" />
              }
            />
            <Route
              path="templates/create"
              element={
                isAuthenticated ? <TemplateCreate /> : <Navigate to="/" />
              }
            />
            <Route
              path="templates/analytics/:templateId"
              element={
                isAuthenticated ? <TemplateAnalyticsPage /> : <Navigate to="/" />
              }
            />
            <Route
              path="workflow/:workflowId/create"
              element={isAuthenticated ? <WorkflowCreate /> : <Navigate to="/" />}
            />
            <Route
              path="workflow/:workflowId/create/:tab"
              element={isAuthenticated ? <WorkflowCreate /> : <Navigate to="/" />}
            />
            <Route
              path="workflow"
              element={isAuthenticated ? <Workflow /> : <Navigate to="/" />}
            />
            <Route
              path="contacts"
              element={isAuthenticated ? <Contacts /> : <Navigate to="/" />}
            />
            <Route
              path="contact-groups"
              element={isAuthenticated ? <BroadcastsPage /> : <Navigate to="/" />}
            />
            <Route
              path="contact-groups/new"
              element={isAuthenticated ? <CreateBroadcastPage /> : <Navigate to="/" />}
            />
            <Route
              path="tasks"
              element={isAuthenticated ? <Tasks /> : <Navigate to="/" />}
            />
            <Route
              path="settings"
              element={isAuthenticated ? <Settings /> : <Navigate to="/" />}
            />
            <Route
              path="ai"
              element={isAuthenticated ? <AiSettingsPage /> : <Navigate to="/" />}
            />
            <Route
              path="onboard"
              element={isAuthenticated ? <Onboard /> : <Navigate to="/" />}
            />
            <Route
              path="workspaces/:workspaceId/api-integration"
              element={isAuthenticated ? <ApiIntegrationPage /> : <Navigate to="/" />}
            />
            <Route
              path="workspaces/:workspaceId/chatbot-widget/connect"
              element={isAuthenticated ? <IntegrationsManageGate><ConnectChatbotWidgetPage /></IntegrationsManageGate> : <Navigate to="/" />}
            />
            <Route
              path="reports"
              element={isAuthenticated ? <Reports /> : <Navigate to="/" />}
            />
            {/* TEMP scratch route for Dashboard redesign — remove once design is finalized and copied over */}
            <Route
              path="dashboard-scratch"
              element={isAuthenticated ? <DashboardScratch /> : <Navigate to="/" />}
            />
            <Route
              path="calls"
              element={isAuthenticated ? <CallsPage /> : <Navigate to="/" />}
            />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      )}
    </Box>
  );
}

// Wrap the App component with BrowserRouter and GoogleOAuthProvider
const AppWrapper = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  if (!googleClientId) {
    console.warn('REACT_APP_GOOGLE_CLIENT_ID is not set. Google Sign-In will not work.');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
  <BrowserRouter>
    <App />
  </BrowserRouter>
    </GoogleOAuthProvider>
);
};

export default AppWrapper;
